import * as React from "react";
import { ReactElement } from "react";
import * as moment from "moment";
import ViewAction from "../commons/components/action/ViewAction";
import ContactInfoActivity, { Type } from "../commons/model/activity/ContactInfoActivity";
import { extractingContent } from "../commons/utils/emails";
import { dateWithOptionalTime } from "../commons/utils/date";
import { fillTemplateString } from "../commons/utils/text-rendering";
import SafeHtmlContent from "../commons/components/SafeHtmlContent";

export default function Activity({
  activity,
  fontsUrl,
}: {
  activity: ContactInfoActivity;
  fontsUrl: String;
}) {
  const style = getStyle(activity);
  return (
    <div key={activity.createdAt} className="activity">
      <div className="timeline">
        <div className="line" />
        <div className={`icon ${style.icon}`} style={{ backgroundColor: style.color }} />
      </div>
      <div className="panel with-shadow vertical-dropdown">
        <div className="vertical-dropdown-toggle">
          <div className="container-vertical-dropdown" data-max-height="12.5rem">
            {getContent(activity, fontsUrl)}
          </div>
        </div>
        <div className="metadata h-flex space-between">
          <div>{dateWithOptionalTime(activity.createdAt)}</div>
          <div>
            {i18n["Activity.Timeline.Item.CreatedBy"]} {activity.createdBy}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ActivityStyle {
  icon: string;
  color: string;
}

export function getStyle(activity: ContactInfoActivity): ActivityStyle {
  switch (activity.type) {
    case Type.REPORT_CREATED:
    case Type.REPORT_AVAILABLE:
    case Type.REPORT_CANCELLED:
    case Type.REPORT_FAILED:
    case Type.REPORT_REJECTED:
    case Type.REPORT_INCOMPLETE:
    case Type.REPORT_VIEWED_BY_PATIENT:
    case Type.REPORT_SHARED_WITH_PATIENT:
      return {
        icon: "ic_file",
        color: "#3153D9",
      };
    case Type.APPOINTMENT:
    case Type.APPOINTMENT_REQUESTED:
    case Type.APPOINTMENT_CANCELED:
      return {
        icon: "ic_calendar",
        color: "#00BFCC",
      };
    case Type.NOTE_ADDED:
      return {
        icon: "ic_edit",
        color: "#FFB300",
      };
    case Type.MESSAGE_EMAIL:
      return {
        icon: "ic_mail",
        color: "#DC016A",
      };
    case Type.PATIENT_INVITED:
    case Type.PATIENT_ACCOUNT_CREATED:
      return {
        icon: "ic_user",
        color: "#8246AF",
      };
    case Type.LEAD_CONVERTED:
      return {
        icon: "ic_user",
        color: "#00A3AD",
      };
  }
}

function getContent(
  activity: ContactInfoActivity,
  fontsUrl: String,
): ReactElement {
  switch (activity.type) {
    case Type.APPOINTMENT:
      return (
        <div>
          <div className="title">{i18n["Activity.Appointment.Booked.Title"]}</div>
          <div>{moment(activity["fromDateTime"]).format("LLL")}</div>
        </div>
      );
    case Type.APPOINTMENT_CANCELED:
      return (
        <div>
          <div className="title">{i18n["Activity.Appointment.Canceled.Title"]}</div>
          <div>{moment(activity["atDateTime"]).format("LLL")}</div>
        </div>
      );
    case Type.APPOINTMENT_REQUESTED:
      return (
        <div>
          <div className="title">{`${activity.createdBy} ${i18n["Activity.Appointment.Request.Title"]}`}</div>
          {activity["content"] && (
            <div className="react-form">
              <label>{i18n["Activity.Appointment.Request.Message"]}</label>
              <div>{activity["content"]}</div>
            </div>
          )}
        </div>
      );
    case Type.NOTE_ADDED:
      return (
        <div className="h-flex space-between">
          <div id="note-activity">{activity["content"]}</div>
        </div>
      );
    case Type.MESSAGE_EMAIL:
      const titleKey = activity["createdByThreadInitiator"]
        ? "Activity.Message.Title.FromDoctor"
        : "Activity.Message.Title.ToDoctor";
      const patientName = activity[activity["createdByThreadInitiator"] ? "to" : "createdBy"];
      return (
        <div>
          <div className="title">{fillTemplateString(i18n[titleKey], { user: patientName })}</div>
          <div className="react-form">
            {activity["subject"] && (
              <div className="input-group">
                <label>{i18n["Activity.Message.Label.Subject"]}</label>
                <div>{activity["subject"]}</div>
              </div>
            )}
            <div className="input-group">
              <label>{i18n["Activity.Message.Label.Message"]}</label>
              <SafeHtmlContent
                unsafeHtml={extractingContent(activity["message"])}
                fontsUrl={fontsUrl}
              ></SafeHtmlContent>
            </div>
          </div>
        </div>
      );
    case Type.PATIENT_INVITED:
      return (
        <div>
          <div className="title">{i18n["Activity.Patient.Invited"]}</div>
        </div>
      );
    case Type.PATIENT_ACCOUNT_CREATED:
      return (
        <div>
          <div className="title">{i18n["Activity.Patient.AccountCreation.Title"]}</div>
        </div>
      );
    case Type.LEAD_CONVERTED:
      return (
        <div>
          <div className="title">{i18n["Activity.Patient.LeadConverted.Title"]}</div>
        </div>
      );
    case Type.REPORT_CREATED:
    case Type.REPORT_AVAILABLE:
    case Type.REPORT_REJECTED:
    case Type.REPORT_FAILED:
    case Type.REPORT_CANCELLED:
    case Type.REPORT_INCOMPLETE:
    case Type.REPORT_SHARED_WITH_PATIENT:
    case Type.REPORT_VIEWED_BY_PATIENT:
      return getReportEventsContent(activity);
  }
}

function getReportEventsContent(activity: ContactInfoActivity) {
  let content: string;
  switch (activity.type) {
    case Type.REPORT_CREATED:
      content = i18n["Activity.Report.Created"];
      break;
    case Type.REPORT_AVAILABLE:
      content = i18n["Activity.Report.Available"];
      break;
    case Type.REPORT_REJECTED:
      content = i18n["Activity.Report.Rejected"];
      break;
    case Type.REPORT_FAILED:
      content = i18n["Activity.Report.Failed"];
      break;
    case Type.REPORT_CANCELLED:
      content = i18n["Activity.Report.Cancelled"];
      break;
    case Type.REPORT_INCOMPLETE:
      content = i18n["Activity.Report.Incomplete"];
      break;
    case Type.REPORT_SHARED_WITH_PATIENT:
      content = fillTemplateString(i18n["Activity.Report.Shared"], {
        user: activity["sharedWith"],
      });
      break;
    case Type.REPORT_VIEWED_BY_PATIENT:
      content = fillTemplateString(i18n["Activity.Report.Viewed"], {
        user: activity.createdBy,
      });
      break;
  }
  return (
    <div id={activity.type.toLocaleLowerCase()} className="h-flex space-between">
      <div>{content}</div>
      {activity["url"] && (
        <ViewAction label={i18n["Activity.Report.Action.ViewReport"]} href={activity["url"]} />
      )}
    </div>
  );
}
